import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { Email as EmailIcon, CheckCircle as CheckCircleIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import useAuthState from '../../../contexts/AuthStateContext';
import { API_BASE_URL } from '../../../constants/api.constants';
import {
  listIntegrationConnectionsApi,
  initiateIntegrationAuthApi,
  disconnectIntegrationConnectionApi,
  listIntegrationMessagesApi,
  getIntegrationMessageApi,
  sendIntegrationMessageApi,
  listIntegrationThreadsApi,
  getIntegrationThreadApi,
} from '../../integrations';
import type {
  NylasConnection,
  NylasMessage,
  EmailParticipant,
  NylasThread,
} from '@equaliq/api-model-v2';

/**
 * Test component for Email Integrations (Nylas)
 */
const TestIntegrations: React.FC = () => {
  const { profile } = useAuthState();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Connection state
  const [connected, setConnected] = useState(false);
  const [connections, setConnections] = useState<NylasConnection[]>([]);

  // Messages state
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [messages, setMessages] = useState<NylasMessage[]>([]);
  const [messageLimit, setMessageLimit] = useState(20);
  const [selectedMessage, setSelectedMessage] = useState<NylasMessage | null>(null);

  // Compose state
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');

  // Threads state
  const [threads, setThreads] = useState<NylasThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<NylasThread | null>(null);
  const [threadLimit, setThreadLimit] = useState(20);
  const [threadMinPriority, setThreadMinPriority] = useState<number | undefined>(undefined);
  const [threadLabel, setThreadLabel] = useState('');
  const [threadAnalyzedOnly, setThreadAnalyzedOnly] = useState(false);

  // Load connections on mount
  useEffect(() => {
    loadConnections();
  }, []);

  // Auto-select first connection
  useEffect(() => {
    if (connections.length > 0 && !selectedConnectionId) {
      setSelectedConnectionId(connections[0].connectionId);
    }
  }, [connections, selectedConnectionId]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const result = await listIntegrationConnectionsApi();
      setConnected(result.connected);
      setConnections(result.connections || []);
    } catch (err) {
      setError('Failed to load connections');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const connectIntegration = async () => {
    try {
      setLoading(true);
      const result = await initiateIntegrationAuthApi({ provider: 'google' });

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        result.authUrl,
        'oauth-nylas',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'oauth-success' && event.data.provider === 'nylas') {
          setSuccess('Successfully connected email account');
          loadConnections();
          popup?.close();
          window.removeEventListener('message', handleMessage);
        } else if (event.data.type === 'oauth-error' && event.data.provider === 'nylas') {
          setError(`Failed to connect: ${event.data.error}`);
          popup?.close();
          window.removeEventListener('message', handleMessage);
        }
      };

      window.addEventListener('message', handleMessage);
    } catch (err) {
      setError('Failed to initiate connection');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const disconnectConnection = async (connectionId: string) => {
    if (!confirm('Disconnect this email account?')) return;

    try {
      setLoading(true);
      await disconnectIntegrationConnectionApi({ connectionId });
      setSuccess('Disconnected successfully');
      loadConnections();
      setMessages([]);
      setSelectedMessage(null);
    } catch (err) {
      setError('Failed to disconnect');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedConnectionId) {
      setError('No connection selected');
      return;
    }

    try {
      setLoading(true);
      const result = await listIntegrationMessagesApi({
        connectionId: selectedConnectionId,
        limit: messageLimit,
      });
      setMessages(result.data || []);
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const viewMessage = async (messageId: string) => {
    if (!selectedConnectionId) return;

    try {
      setLoading(true);
      const result = await getIntegrationMessageApi({
        connectionId: selectedConnectionId,
        messageId,
      });
      setSelectedMessage(result.data);
    } catch (err) {
      setError('Failed to load message');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!selectedConnectionId) {
      setError('No connection selected');
      return;
    }

    if (!composeTo.trim() || !composeSubject.trim() || !composeBody.trim()) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      await sendIntegrationMessageApi({
        connectionId: selectedConnectionId,
        to: [{ email: composeTo.trim() }],
        subject: composeSubject.trim(),
        body: composeBody.trim(),
      });
      setSuccess('Email sent successfully');
      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
    } catch (err) {
      setError('Failed to send email');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatParticipants = (participants?: EmailParticipant[]) => {
    if (!participants || participants.length === 0) return 'Unknown';
    return participants.map(p => p.name || p.email).join(', ');
  };

  const loadThreads = async () => {
    if (!selectedConnectionId) {
      setError('No connection selected');
      return;
    }

    try {
      setLoading(true);
      const result = await listIntegrationThreadsApi({
        connectionId: selectedConnectionId,
        limit: threadLimit,
        minPriority: threadMinPriority,
        label: threadLabel || undefined,
        analyzedOnly: threadAnalyzedOnly || undefined,
      });
      setThreads(result.threads || []);
      setSuccess(`Loaded ${result.threads?.length || 0} threads`);
    } catch (err) {
      setError('Failed to load threads');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const viewThread = async (threadMetadataId: string) => {
    try {
      setLoading(true);
      const result = await getIntegrationThreadApi({ threadMetadataId });
      setSelectedThread(result.thread);
    } catch (err) {
      setError('Failed to load thread');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatISODate = (isoString?: string) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Email Integration (Nylas)
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{
          mb: 3,
          '& .MuiTab-root': {
            color: 'white',
            '&.Mui-selected': {
              color: 'white'
            }
          }
        }}>
        <Tab label="Connections" />
        <Tab label="Inbox" />
        <Tab label="Threads" />
        <Tab label="Compose" />
      </Tabs>

      {/* Connections Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Email Connections</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadConnections}
                  disabled={loading}
                  size="small"
                >
                  Refresh
                </Button>
                <Button
                  startIcon={<EmailIcon />}
                  variant="contained"
                  onClick={connectIntegration}
                  disabled={loading}
                  size="small"
                >
                  Connect Email
                </Button>
              </Stack>
            </Stack>

            {loading && <CircularProgress />}

            {!loading && !connected && (
              <Alert severity="info">No email accounts connected. Click "Connect Email" to get started.</Alert>
            )}

            {!loading && connections.length > 0 && (
              <List>
                {connections.map((conn) => (
                  <ListItem key={conn.connectionId} divider>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography>{conn.email}</Typography>
                          {conn.enabled && <CheckCircleIcon color="success" fontSize="small" />}
                        </Stack>
                      }
                      secondary={
                        <>
                          Provider: {conn.provider} | Connected: {new Date(conn.connectedAt).toLocaleString()}
                          <br />
                          Connection ID: {conn.connectionId}
                        </>
                      }
                    />
                    <Button
                      size="small"
                      color="error"
                      onClick={() => disconnectConnection(conn.connectionId)}
                      disabled={loading}
                    >
                      Disconnect
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Inbox Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            {!selectedMessage ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                  {connections.length > 0 && (
                    <>
                      <FormControl sx={{ minWidth: 250 }}>
                        <InputLabel>Email Account</InputLabel>
                        <Select
                          value={selectedConnectionId}
                          onChange={(e) => setSelectedConnectionId(e.target.value)}
                          label="Email Account"
                        >
                          {connections.map((conn) => (
                            <MenuItem key={conn.connectionId} value={conn.connectionId}>
                              {conn.email}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl sx={{ minWidth: 100 }}>
                        <InputLabel>Show</InputLabel>
                        <Select
                          value={messageLimit}
                          onChange={(e) => setMessageLimit(e.target.value as number)}
                          label="Show"
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </FormControl>

                      <Button
                        variant="contained"
                        onClick={loadMessages}
                        disabled={loading || !selectedConnectionId}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Load Messages'}
                      </Button>
                    </>
                  )}
                </Stack>

                {connections.length === 0 && (
                  <Alert severity="info">Connect an email account to view messages</Alert>
                )}

                {messages.length > 0 && (
                  <List>
                    {messages.map((msg) => (
                      <React.Fragment key={msg.id}>
                        <ListItem onClick={() => viewMessage(msg.id)}>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>{msg.subject || '(no subject)'}</Typography>
                                {msg.unread && <Chip label="Unread" size="small" color="primary" />}
                              </Stack>
                            }
                            secondary={
                              <>
                                From: {formatParticipants(msg.from)}
                                <br />
                                {msg.snippet}
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(msg.date)}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                )}

                {messages.length === 0 && !loading && selectedConnectionId && (
                  <Typography color="text.secondary">No messages loaded. Click "Load Messages" to fetch.</Typography>
                )}
              </>
            ) : (
              <>
                <Button size="small" onClick={() => setSelectedMessage(null)} sx={{ mb: 2 }}>
                  ← Back to list
                </Button>

                <Typography variant="h6" gutterBottom>
                  {selectedMessage.subject || '(no subject)'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>From:</strong> {formatParticipants(selectedMessage.from)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>To:</strong> {formatParticipants(selectedMessage.to)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Date:</strong> {formatDate(selectedMessage.date)}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'background.paper',
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedMessage.body || '(no content)' }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Threads Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            {!selectedThread ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
                  {connections.length > 0 && (
                    <>
                      <FormControl sx={{ minWidth: 250 }}>
                        <InputLabel>Email Account</InputLabel>
                        <Select
                          value={selectedConnectionId}
                          onChange={(e) => setSelectedConnectionId(e.target.value)}
                          label="Email Account"
                        >
                          {connections.map((conn) => (
                            <MenuItem key={conn.connectionId} value={conn.connectionId}>
                              {conn.email}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl sx={{ minWidth: 100 }}>
                        <InputLabel>Show</InputLabel>
                        <Select
                          value={threadLimit}
                          onChange={(e) => setThreadLimit(e.target.value as number)}
                          label="Show"
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Min Priority</InputLabel>
                        <Select
                          value={threadMinPriority || ''}
                          onChange={(e) => setThreadMinPriority(e.target.value ? Number(e.target.value) : undefined)}
                          label="Min Priority"
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value={1}>1 - Low</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3 - Medium</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                          <MenuItem value={5}>5 - High</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Filter by Label"
                        value={threadLabel}
                        onChange={(e) => setThreadLabel(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                        placeholder="e.g., Deal, Legal"
                      />

                      <FormControl>
                        <Button
                          variant={threadAnalyzedOnly ? "contained" : "outlined"}
                          size="small"
                          onClick={() => setThreadAnalyzedOnly(!threadAnalyzedOnly)}
                        >
                          {threadAnalyzedOnly ? '✓ Analyzed Only' : 'All'}
                        </Button>
                      </FormControl>

                      <Button
                        variant="contained"
                        onClick={loadThreads}
                        disabled={loading || !selectedConnectionId}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Load Threads'}
                      </Button>
                    </>
                  )}
                </Stack>

                {connections.length === 0 && (
                  <Alert severity="info">Connect an email account to view threads</Alert>
                )}

                {threads.length > 0 && (
                  <List>
                    {threads.map((thread) => (
                      <React.Fragment key={thread.threadMetadataId}>
                        <ListItem
                          onClick={() => viewThread(thread.threadMetadataId)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="body1">
                                  {thread.summary || `Thread ${thread.externalThreadId.substring(0, 8)}...`}
                                </Typography>
                                {thread.priority && (
                                  <Chip
                                    label={`P${thread.priority}`}
                                    size="small"
                                    color={thread.priority >= 4 ? "error" : thread.priority >= 3 ? "warning" : "default"}
                                  />
                                )}
                                {!thread.analyzed && (
                                  <Chip label="Unanalyzed" size="small" color="info" />
                                )}
                                {thread.labels && thread.labels.length > 0 && thread.labels.map((label, i) => (
                                  <Chip key={i} label={label} size="small" variant="outlined" />
                                ))}
                              </Stack>
                            }
                            secondary={
                              <>
                                Messages: {thread.messageCount} | Last: {formatISODate(thread.lastMessageAt)}
                                {thread.ragKeywords && thread.ragKeywords.length > 0 && (
                                  <>
                                    <br />
                                    Keywords: {thread.ragKeywords.join(', ')}
                                  </>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                )}

                {threads.length === 0 && !loading && selectedConnectionId && (
                  <Typography color="text.secondary">No threads loaded. Click "Load Threads" to fetch.</Typography>
                )}
              </>
            ) : (
              <>
                <Button size="small" onClick={() => setSelectedThread(null)} sx={{ mb: 2 }}>
                  ← Back to list
                </Button>

                <Typography variant="h6" gutterBottom>
                  Thread Details
                </Typography>

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Thread ID</Typography>
                    <Typography variant="body2">{selectedThread.threadMetadataId}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">External Thread ID</Typography>
                    <Typography variant="body2">{selectedThread.externalThreadId}</Typography>
                  </Box>

                  {selectedThread.summary && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Summary</Typography>
                      <Typography variant="body1">{selectedThread.summary}</Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                    <Chip
                      label={selectedThread.priority ? `Priority ${selectedThread.priority}` : 'Not set'}
                      color={
                        selectedThread.priority && selectedThread.priority >= 4
                          ? "error"
                          : selectedThread.priority && selectedThread.priority >= 3
                            ? "warning"
                            : "default"
                      }
                    />
                  </Box>

                  {selectedThread.labels && selectedThread.labels.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Labels</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selectedThread.labels.map((label, i) => (
                          <Chip key={i} label={label} size="small" />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {selectedThread.ragKeywords && selectedThread.ragKeywords.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Keywords</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selectedThread.ragKeywords.map((keyword, i) => (
                          <Chip key={i} label={keyword} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Message Count</Typography>
                    <Typography variant="body2">{selectedThread.messageCount}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Last Message</Typography>
                    <Typography variant="body2">{formatISODate(selectedThread.lastMessageAt)}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Analysis Status</Typography>
                    <Chip
                      label={selectedThread.analyzed ? `Analyzed ${selectedThread.analyzedAt ? `at ${formatISODate(selectedThread.analyzedAt)}` : ''}` : 'Not analyzed'}
                      color={selectedThread.analyzed ? "success" : "default"}
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                    <Typography variant="body2">{formatISODate(selectedThread.createdAt)}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Updated</Typography>
                    <Typography variant="body2">{formatISODate(selectedThread.updatedAt)}</Typography>
                  </Box>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Compose Tab */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            {connections.length > 0 ? (
              <Stack spacing={2}>
                {connections.length > 1 && (
                  <FormControl fullWidth>
                    <InputLabel>Send From</InputLabel>
                    <Select
                      value={selectedConnectionId}
                      onChange={(e) => setSelectedConnectionId(e.target.value)}
                      label="Send From"
                    >
                      {connections.map((conn) => (
                        <MenuItem key={conn.connectionId} value={conn.connectionId}>
                          {conn.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  label="To"
                  value={composeTo}
                  onChange={(e) => setComposeTo(e.target.value)}
                  fullWidth
                  type="email"
                  placeholder="recipient@example.com"
                />
                <TextField
                  label="Subject"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Message"
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="Write your message..."
                />
                <Button
                  variant="contained"
                  color="success"
                  onClick={sendEmail}
                  disabled={loading || !selectedConnectionId}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : 'Send Email'}
                </Button>
              </Stack>
            ) : (
              <Alert severity="info">Connect an email account to send messages</Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TestIntegrations;
